import {useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult} from '@tanstack/react-query';
import {AxiosError} from 'axios';
import {useDispatch} from 'react-redux';
import {toast} from 'react-toastify';
import {useTranslation} from 'react-i18next';
import {ConnectionRequestStatus, IConnection} from '../../incase-domain/connection';
import i18n, {type TypedTranslation} from '../../i18n';
import {
    INotification,
    INotificationResponse,
    ISendPresentationsNotification,
    ISharePresentationsNotification,
    NotificationStatus,
} from '../../incase-domain/notification';
import {PresentationsRequestStatus} from '../../incase-domain/presentation';
import {IPaginatedSearch} from '../../incase-domain/search';
import {setConnectionIdAction} from '../../store/actions/connection.actions';
import {updateConnectionRequest} from '../connection.service';
import {fetchNotifications, markNotificationAsSeen, updateNotification} from '../notification.service';
import {updateSendPresentationsRequest, updateSharePresentationsRequest} from '../presentation';
import {QUERY_KEYS} from '../../utils/queryKeys';

type IUseNotificationReturnType = {
    notifications: UseQueryResult<INotificationResponse, unknown>;
    mutateConnectionRequest: UseMutationResult<
        IConnection,
        unknown,
        {connectionRequestId: string; status: ConnectionRequestStatus},
        unknown
    >;
    mutateSendPresentationsRequest: UseMutationResult<
        boolean,
        AxiosError<unknown, unknown>,
        {notification: ISendPresentationsNotification; status: PresentationsRequestStatus},
        unknown
    >;
    mutateSharePresentationsRequest: UseMutationResult<
        boolean,
        // eslint-disable-next-line
        AxiosError<unknown, any>,
        {notification: ISharePresentationsNotification; status: PresentationsRequestStatus},
        unknown
    >;
    mutateNotification: UseMutationResult<
        INotification,
        unknown,
        {notification: INotification; onDeletePageChangeHandler: (deletedCount?: number) => void},
        unknown
    >;
    mutateNotificationAsSeen: UseMutationResult<void, unknown, {notificationIds: string[]}, unknown>;
};

function useNotification(paginatedSearch: IPaginatedSearch, status: NotificationStatus[]): IUseNotificationReturnType {
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const query = useQueryClient();

    const notifications = useQuery({
        queryKey: QUERY_KEYS.notification.list({paginatedSearch, status}),
        queryFn: () => fetchNotifications(paginatedSearch, status),
        refetchInterval: 10000,
    });

    const mutateConnectionRequest = useMutation(
        (vars: {connectionRequestId: string; status: ConnectionRequestStatus}) =>
            updateConnectionRequest(vars.connectionRequestId, vars.status),
        {
            onSuccess: (connection): void => {
                query.invalidateQueries({queryKey: QUERY_KEYS.connection.all});
                query.invalidateQueries({queryKey: QUERY_KEYS.notification.lists()});
                query.invalidateQueries({queryKey: QUERY_KEYS.auth.all});

                if (connection) {
                    dispatch(setConnectionIdAction(connection._id));
                }
            },
        }
    );

    const mutateSendPresentationsRequest = useMutation(
        (vars: {notification: ISendPresentationsNotification; status: PresentationsRequestStatus}) =>
            updateSendPresentationsRequest(vars.notification, vars.status),
        {
            onSuccess: (data, variables): void => {
                query.invalidateQueries({queryKey: QUERY_KEYS.notification.lists()});
                if (variables.status === PresentationsRequestStatus.ACCEPTED) {
                    query.invalidateQueries({queryKey: QUERY_KEYS.presentation.lists()});
                    toast.info(t('editorPage.sharePresentations.sharePresentationModal.toastMessage.successfullyAccepted'));
                } else {
                    toast.info(t('editorPage.sharePresentations.sharePresentationModal.toastMessage.unsuccessfullyAccepted'));
                }
            },
            onError: (error: AxiosError<{message?: string}>, variables) => {
                const message = error.response?.data.message;
                if (variables.status === PresentationsRequestStatus.ACCEPTED && message && i18n.exists(message)) {
                    toast.error(t(message as TypedTranslation));
                } else {
                    toast.info(t('editorPage.sharePresentations.sharePresentationModal.toastMessage.unsuccessfullyAcceptedShared'));
                }
            },
        }
    );

    const mutateSharePresentationsRequest = useMutation(
        (vars: {notification: ISharePresentationsNotification; status: PresentationsRequestStatus}) =>
            updateSharePresentationsRequest(vars.notification, vars.status),
        {
            onSuccess: (data, variables): void => {
                query.invalidateQueries({queryKey: QUERY_KEYS.notification.lists()});

                if (variables.status === PresentationsRequestStatus.ACCEPTED) {
                    query.invalidateQueries({queryKey: QUERY_KEYS.sharedPresentation.lists()});
                    toast.info(t('editorPage.sharePresentations.sharePresentationModal.toastMessage.successfullyAcceptedShared'));
                } else {
                    toast.info(t('editorPage.sharePresentations.sharePresentationModal.toastMessage.unsuccessfullyAcceptedShared'));
                }
            },
            onError: (error: AxiosError<{message?: string}>) => {
                const message = error.response?.data.message;
                if (message && i18n.exists(message)) {
                    toast.error(t(message as TypedTranslation));
                } else {
                    toast.info(t('editorPage.sharePresentations.sharePresentationModal.toastMessage.unsuccessfullyAccepted'));
                }
            },
        }
    );

    const mutateNotification = useMutation(
        ({notification}: {notification: INotification; onDeletePageChangeHandler: (deletedCount?: number) => void}) =>
            updateNotification(notification),
        {
            onSuccess: (_, {onDeletePageChangeHandler}) => {
                query.invalidateQueries({queryKey: QUERY_KEYS.notification.lists()});
                if (onDeletePageChangeHandler) {
                    onDeletePageChangeHandler();
                }
            },
        }
    );

    const mutateNotificationAsSeen = useMutation((vars: {notificationIds: string[]}) => markNotificationAsSeen(vars.notificationIds), {
        onSuccess: (): void => {
            query.invalidateQueries({queryKey: QUERY_KEYS.notification.lists()});
        },
    });

    return {
        notifications,
        mutateConnectionRequest,
        mutateSendPresentationsRequest,
        mutateSharePresentationsRequest,
        mutateNotification,
        mutateNotificationAsSeen,
    };
}

export default useNotification;
